import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Chip, Divider, CircularProgress,
  Checkbox, FormControlLabel, FormGroup, Accordion, AccordionSummary,
  AccordionDetails, Card, CardContent, TextField, ToggleButton, ToggleButtonGroup,
  Alert, Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Bolt as PowerIcon,
  Opacity as WaterIcon,
  Cloud as SteamIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, Cell, PieChart, Pie, LabelList,
} from 'recharts';
import axios from 'axios';

// Types
interface Section { id: number; section_name: string; }
interface Machine { id: number; machine_name: string; section_id: number; }
interface HierarchyData { sections: Section[]; machines: Machine[]; dateRange: { min_date: string; max_date: string }; }
interface ChartRow { period: string; steam?: number; power?: number; water?: number; per_meter?: number; consumption?: number; production_mts?: number; }
interface KPIData { [key: string]: any; }
interface BreakdownRow { name: string; steam?: number; power?: number; water?: number; consumption?: number; production_mts?: number; per_meter?: number; }

const VIEW_MODES = [
  { key: 'overall_energy', label: 'Overall Energy',  icon: '⚡' },
  { key: 'per_meter_steam',  label: 'Per Meter – Steam', icon: '💨' },
  { key: 'per_meter_power',  label: 'Per Meter – Power', icon: '🔌' },
  { key: 'per_meter_water',  label: 'Per Meter – Water', icon: '💧' },
];
const GROUP_BY_OPTIONS = [
  { key: 'day',   label: 'By Day'   },
  { key: 'month', label: 'By Month' },
  { key: 'year',  label: 'By Year'  },
];
const SECTION_COLORS = ['#1565C0','#F57C00','#00897B','#6A1B9A','#D32F2F','#2E7D32','#00838F','#AD1457'];
const METER_TYPE_COLORS: Record<string,string> = { steam:'#1565C0', power:'#F57C00', water:'#00897B' };
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmt(n: number, d = 0) {
  if (!n) return '0';
  if (n >= 1_000_000) return (n/1_000_000).toFixed(1)+'M';
  if (n >= 1_000)     return (n/1_000).toFixed(1)+'K';
  return n.toFixed(d);
}

const AnalyticsDashboard: React.FC = () => {
  const [hierarchy, setHierarchy]           = useState<HierarchyData | null>(null);
  const [chartData, setChartData]           = useState<ChartRow[]>([]);
  const [kpiData, setKpiData]               = useState<KPIData>({});
  const [breakdownData, setBreakdownData]   = useState<BreakdownRow[]>([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  const [viewMode, setViewMode]             = useState('overall_energy');
  const [groupBy, setGroupBy]               = useState('month');
  const [selectedSections, setSelectedSections]     = useState<number[]>([]);
  const [selectedMachines, setSelectedMachines]     = useState<number[]>([]);
  const [selectedMeterTypes, setSelectedMeterTypes] = useState<string[]>([]);
  const [selectedYears, setSelectedYears]           = useState<number[]>([]);
  const [selectedMonths, setSelectedMonths]         = useState<number[]>([]);
  const [startDate, setStartDate]           = useState('');
  const [endDate, setEndDate]               = useState('');

  const token = localStorage.getItem('token');
  const authHdr = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get('/api/energy-management/analytics/filters', authHdr).then(r => {
      setHierarchy(r.data);
      if (r.data.dateRange?.min_date) setStartDate(r.data.dateRange.min_date);
      if (r.data.dateRange?.max_date) setEndDate(r.data.dateRange.max_date);
    }).catch(() => setError('Failed to load filter data'));
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { view_type: viewMode, group_by: groupBy };
      if (startDate) params.start_date = startDate;
      if (endDate)   params.end_date   = endDate;
      if (selectedSections.length)   params.section_ids  = selectedSections.join(',');
      if (selectedMachines.length)   params.machine_ids  = selectedMachines.join(',');
      if (selectedMeterTypes.length) params.meter_types  = selectedMeterTypes.join(',');
      if (selectedYears.length)      params.years        = selectedYears.join(',');
      if (selectedMonths.length)     params.months       = selectedMonths.join(',');
      const r = await axios.get('/api/energy-management/analytics/advanced', { ...authHdr, params });
      setChartData(r.data.chartData || []);
      setKpiData(r.data.kpiData || {});
      setBreakdownData(r.data.breakdownData || []);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [viewMode, groupBy, startDate, endDate, selectedSections, selectedMachines, selectedMeterTypes, selectedYears, selectedMonths]);

  useEffect(() => { if (hierarchy) fetchAnalytics(); }, [hierarchy, viewMode, groupBy, startDate, endDate, selectedSections, selectedMachines, selectedMeterTypes, selectedYears, selectedMonths]);

  const isPerMeter = viewMode.startsWith('per_meter');
  const perMeterType = viewMode === 'per_meter_steam' ? 'Steam' : viewMode === 'per_meter_power' ? 'Power' : 'Water';
  const perMeterUnit = viewMode === 'per_meter_steam' ? 'kg' : viewMode === 'per_meter_power' ? 'kwh' : 'L';

  const toggleNum = (list: number[], set: (v:number[])=>void, id: number) =>
    set(list.includes(id) ? list.filter(x=>x!==id) : [...list, id]);
  const toggleStr = (list: string[], set: (v:string[])=>void, val: string) =>
    set(list.includes(val) ? list.filter(x=>x!==val) : [...list, val]);

  const visibleMachines = hierarchy?.machines.filter(m =>
    selectedSections.length === 0 || selectedSections.includes(m.section_id)) || [];

  const activeTypesBars = ['steam','power','water'].filter(t =>
    selectedMeterTypes.length === 0 || selectedMeterTypes.map(x=>x.toLowerCase()).includes(t));

  // ── KPI Cards ────────────────────────────────────────────────────────────
  const KpiCards = () => {
    if (isPerMeter) {
      const k = kpiData as any;
      return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label:`Total ${perMeterType}`, value: k.total_consumption, unit: perMeterUnit, color:'#1565C0' },
            { label:'Total Production (Mts)', value: k.total_production_mts, unit:'Mts', color:'#F57C00' },
            { label:`Avg ${perMeterType}/Mts`, value: k.avg_per_meter, unit:`${perMeterUnit}/Mts`, color:'#00897B', dec:4 },
            { label:'Periods', value: k.periods, unit: '', color:'#6A1B9A' },
          ].map((c,i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card elevation={2} sx={{ borderLeft:`4px solid ${c.color}` }}>
                <CardContent sx={{ py:1.5 }}>
                  <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                  <Typography variant="h5" fontWeight="bold" color={c.color}>{fmt(c.value||0, c.dec)}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.unit}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }
    return (
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { key:'steam', label:'Total Steam',  icon:<SteamIcon/>, color:'#1565C0', unit:'kg'  },
          { key:'power', label:'Total Power',  icon:<PowerIcon/>, color:'#F57C00', unit:'kwh' },
          { key:'water', label:'Total Water',  icon:<WaterIcon/>, color:'#00897B', unit:'L'   },
        ].map(t => {
          const d = kpiData[t.key]; if(!d) return null;
          return (
            <Grid item xs={12} sm={4} key={t.key}>
              <Card elevation={2} sx={{ borderLeft:`4px solid ${t.color}` }}>
                <CardContent sx={{ py:1.5 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box color={t.color}>{t.icon}</Box>
                    <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold" color={t.color}>{fmt(d.total)}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.unit} · avg {fmt(d.avg_daily)}/day · {d.days} days</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // ── Main chart ────────────────────────────────────────────────────────────
  const MainChart = ({ title }: { title: string }) => (
    <Paper elevation={2} sx={{ p:2, mb:2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{title}</Typography>
      <ResponsiveContainer width="100%" height={300}>
        {isPerMeter ? (
          <ComposedChart data={chartData} margin={{ top:20, right:40, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="period" tick={{ fontSize:11 }}/>
            <YAxis yAxisId="left"  tick={{ fontSize:11 }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize:11 }}/>
            <ReTooltip
              formatter={(v:any, name:any) => {
                if (typeof v !== 'number') return [String(v), String(name)];
                if (String(name).includes('/Mts')) return [v.toFixed(4), String(name)];
                return [fmt(v, 2), String(name)];
              }}
            />
            <Legend/>
            <Bar yAxisId="left" dataKey="consumption" name={`${perMeterType} (${perMeterUnit})`} fill={METER_TYPE_COLORS[perMeterType.toLowerCase()]} radius={[3,3,0,0]}>
              <LabelList dataKey="consumption" position="top" style={{ fontSize:10, fill:'#555' }} formatter={(v:any)=>fmt(Number(v))}/>
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="production_mts" name="Production (Mts)" stroke="#0D47A1" strokeWidth={2.5} dot={{ r:3 }}/>
          </ComposedChart>
        ) : (
          <ComposedChart data={chartData} margin={{ top:20, right:30, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="period" tick={{ fontSize:11 }}/>
            <YAxis tick={{ fontSize:11 }}/>
            <ReTooltip formatter={(v:any)=>fmt(Number(v))}/>
            <Legend/>
            {activeTypesBars.map(t => (
              <Bar key={t} dataKey={t} name={t.charAt(0).toUpperCase()+t.slice(1)+' '+({steam:'(kg)',power:'(kwh)',water:'(L)'}[t]||'')} fill={METER_TYPE_COLORS[t]} radius={[3,3,0,0]}>
                <LabelList dataKey={t} position="top" style={{ fontSize:10, fill:'#555' }} formatter={(v:any)=>Number(v)>0?fmt(Number(v)):''}/>
                </Bar>
            ))}
          </ComposedChart>
        )}
      </ResponsiveContainer>
    </Paper>
  );

  // ── Section breakdown ────────────────────────────────────────────────────
  const SectionBreakdown = () => {
    if (!breakdownData.length) return null;
    return (
      <Grid container spacing={2} sx={{ mb:2 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p:2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Section-wise {isPerMeter ? `${perMeterType} per Meter` : 'Consumption'}</Typography>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={breakdownData} layout="vertical" margin={{ left:120, right:30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
                <XAxis type="number" tick={{ fontSize:11 }}/>
                <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={115}/>
                <ReTooltip formatter={(v:any)=>typeof v==='number'&&v<1?v.toFixed(4):fmt(Number(v))}/>
                {isPerMeter ? (
                  <Bar dataKey="per_meter" name={`${perMeterUnit}/Mts`} radius={[0,4,4,0]}>
                    {breakdownData.map((_,i)=><Cell key={i} fill={SECTION_COLORS[i%SECTION_COLORS.length]}/>)}
                  </Bar>
                ) : (
                  <>
                    {activeTypesBars.map(t=>(
                      <Bar key={t} dataKey={t} name={t.charAt(0).toUpperCase()+t.slice(1)} fill={METER_TYPE_COLORS[t]} stackId="s"/>
                    ))}
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p:2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Share by Section</Typography>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={breakdownData.map(d=>({ name:d.name, value: isPerMeter ? ((d as any).production_mts||0) : (d.steam||0)+(d.power||0)+(d.water||0) }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85}
                  label={({percent}:{name?:string,percent?:number})=>`${((percent||0)*100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                  {breakdownData.map((_,i)=><Cell key={i} fill={SECTION_COLORS[i%SECTION_COLORS.length]}/>)}
                </Pie>
                <ReTooltip formatter={(v:any)=>fmt(Number(v))}/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // ── Data table ─────────────────────────────────────────────────────────────
  const DataTable = () => {
    if (!chartData.length) return null;
    const th = { padding:'6px 12px', textAlign:'right' as const, borderBottom:'2px solid #ddd', background:'#f5f5f5' };
    const td = { padding:'5px 12px', textAlign:'right' as const, borderBottom:'1px solid #eee' };
    return (
      <Paper elevation={2} sx={{ p:2 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Data Summary – {chartData.length} {groupBy === 'day' ? 'days' : groupBy === 'month' ? 'months' : 'years'}
        </Typography>
        <Box sx={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign:'left' }}>Period</th>
                {isPerMeter ? <>
                  <th style={th}>Consumption ({perMeterUnit})</th>
                  <th style={th}>Production (Mts)</th>
                  <th style={th}>{perMeterType}/Mts</th>
                </> : <>
                  {activeTypesBars.includes('steam') && <th style={{...th, color:'#1565C0'}}>Steam (kg)</th>}
                  {activeTypesBars.includes('power') && <th style={{...th, color:'#F57C00'}}>Power (kwh)</th>}
                  {activeTypesBars.includes('water') && <th style={{...th, color:'#00897B'}}>Water (L)</th>}
                  <th style={th}>Total</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row,i) => (
                <tr key={i} style={{ background: i%2===0?'#fff':'#fafafa' }}>
                  <td style={{ ...td, textAlign:'left', fontWeight:500 }}>{row.period}</td>
                  {isPerMeter ? <>
                    <td style={td}>{fmt(row.consumption||0)}</td>
                    <td style={td}>{fmt((row as any).production_mts||0)}</td>
                    <td style={{ ...td, color:'#D32F2F', fontWeight:600 }}>{((row as any).per_meter||0).toFixed(4)}</td>
                  </> : <>
                    {activeTypesBars.includes('steam') && <td style={{...td,color:'#1565C0'}}>{fmt(row.steam||0)}</td>}
                    {activeTypesBars.includes('power') && <td style={{...td,color:'#F57C00'}}>{fmt(row.power||0)}</td>}
                    {activeTypesBars.includes('water') && <td style={{...td,color:'#00897B'}}>{fmt(row.water||0)}</td>}
                    <td style={{ ...td, fontWeight:600 }}>{fmt((row.steam||0)+(row.power||0)+(row.water||0))}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
    );
  };

  const groupLabel = GROUP_BY_OPTIONS.find(g=>g.key===groupBy)?.label||'';
  const hasFilters = selectedSections.length||selectedMachines.length||selectedMeterTypes.length||selectedYears.length||selectedMonths.length;

  return (
    <Box sx={{ display: { xs: 'block', md: 'flex' }, gap:2, height:'100%', minHeight:0 }}>
      {/* ── Filter Panel ── */}
      <Box
        sx={{
          width: { xs: '100%', md: 230 },
          flexShrink: 0,
          display:'flex',
          flexDirection:'column',
          gap:1,
          overflowY: { xs: 'visible', md: 'auto' },
          maxHeight: { xs: 'none', md: 'calc(100vh - 100px)' },
          pr: { xs: 0, md: 0.5 },
          mb: { xs: 2, md: 0 },
        }}
      >

        <Paper elevation={1} sx={{ p:1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ display:'flex', alignItems:'center', gap:0.5, mb:1 }}>
            <FilterIcon sx={{ fontSize:14 }}/> Date Range
          </Typography>
          <TextField size="small" type="date" label="From" fullWidth value={startDate} onChange={e=>setStartDate(e.target.value)} sx={{ mb:1 }} InputLabelProps={{ shrink:true }}/>
          <TextField size="small" type="date" label="To"   fullWidth value={endDate}   onChange={e=>setEndDate(e.target.value)}   InputLabelProps={{ shrink:true }}/>
        </Paper>

        <Accordion defaultExpanded disableGutters elevation={1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ minHeight:36, py:0 }}>
            <Typography variant="caption" fontWeight="bold">Meter Type</Typography>
            {selectedMeterTypes.length>0&&<Chip label={selectedMeterTypes.length} size="small" color="primary" sx={{ ml:1, height:18, fontSize:10 }}/>}
          </AccordionSummary>
          <AccordionDetails sx={{ pt:0 }}>
            <FormGroup>
              {['Steam','Power','Water'].map(t=>(
                <FormControlLabel key={t} control={<Checkbox size="small" checked={selectedMeterTypes.includes(t)} onChange={()=>toggleStr(selectedMeterTypes,setSelectedMeterTypes,t)}/>}
                  label={<Typography variant="caption">{t==='Steam'?'💨':t==='Power'?'⚡':'💧'} {t}</Typography>}/>
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded disableGutters elevation={1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ minHeight:36, py:0 }}>
            <Typography variant="caption" fontWeight="bold">Year</Typography>
            {selectedYears.length>0&&<Chip label={selectedYears.length} size="small" color="primary" sx={{ ml:1, height:18, fontSize:10 }}/>}
          </AccordionSummary>
          <AccordionDetails sx={{ pt:0 }}>
            <FormGroup>
              {[2024,2025,2026].map(y=>(
                <FormControlLabel key={y} control={<Checkbox size="small" checked={selectedYears.includes(y)} onChange={()=>toggleNum(selectedYears,setSelectedYears,y)}/>}
                  label={<Typography variant="caption">{y}</Typography>}/>
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters elevation={1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ minHeight:36, py:0 }}>
            <Typography variant="caption" fontWeight="bold">Month</Typography>
            {selectedMonths.length>0&&<Chip label={selectedMonths.length} size="small" color="primary" sx={{ ml:1, height:18, fontSize:10 }}/>}
          </AccordionSummary>
          <AccordionDetails sx={{ pt:0 }}>
            <FormGroup>
              {MONTHS.map((m,i)=>(
                <FormControlLabel key={m} control={<Checkbox size="small" checked={selectedMonths.includes(i+1)} onChange={()=>toggleNum(selectedMonths,setSelectedMonths,i+1)}/>}
                  label={<Typography variant="caption">{m}</Typography>}/>
              ))}
            </FormGroup>
          </AccordionDetails>
        </Accordion>

        {hierarchy?.sections.length ? (
          <Accordion disableGutters elevation={1}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ minHeight:36, py:0 }}>
              <Typography variant="caption" fontWeight="bold">Section</Typography>
              {selectedSections.length>0&&<Chip label={selectedSections.length} size="small" color="primary" sx={{ ml:1, height:18, fontSize:10 }}/>}
            </AccordionSummary>
            <AccordionDetails sx={{ pt:0 }}>
              <FormGroup>
                {hierarchy.sections.map(s=>(
                  <FormControlLabel key={s.id} control={<Checkbox size="small" checked={selectedSections.includes(s.id)} onChange={()=>toggleNum(selectedSections,setSelectedSections,s.id)}/>}
                    label={<Typography variant="caption">{s.section_name}</Typography>}/>
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        ) : null}

        {visibleMachines.length>0 && (
          <Accordion disableGutters elevation={1}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{ minHeight:36, py:0 }}>
              <Typography variant="caption" fontWeight="bold">Machine</Typography>
              {selectedMachines.length>0&&<Chip label={selectedMachines.length} size="small" color="primary" sx={{ ml:1, height:18, fontSize:10 }}/>}
            </AccordionSummary>
            <AccordionDetails sx={{ pt:0 }}>
              <FormGroup>
                {visibleMachines.map(m=>(
                  <FormControlLabel key={m.id} control={<Checkbox size="small" checked={selectedMachines.includes(m.id)} onChange={()=>toggleNum(selectedMachines,setSelectedMachines,m.id)}/>}
                    label={<Typography variant="caption">{m.machine_name}</Typography>}/>
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        )}

        {hasFilters ? (
          <Button size="small" variant="outlined" color="warning" onClick={()=>{
            setSelectedSections([]); setSelectedMachines([]); setSelectedMeterTypes([]);
            setSelectedYears([]); setSelectedMonths([]);
          }}>Clear All Filters</Button>
        ) : null}
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex:1, overflowY:'auto', minWidth:0 }}>
        {/* Toolbar */}
        <Paper elevation={1} sx={{ p:1.5, mb:2, display:'flex', alignItems:'center', flexWrap:'wrap', gap:1 }}>
          <Box sx={{ display:'flex', gap:0.5, flexWrap:'wrap' }}>
            {VIEW_MODES.map(v=>(
              <Button key={v.key} size="small" variant={viewMode===v.key?'contained':'outlined'}
                sx={{ fontSize:11, py:0.5 }} onClick={()=>setViewMode(v.key)}>
                {v.icon} {v.label}
              </Button>
            ))}
          </Box>
          <Divider orientation="vertical" flexItem/>
          <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_,v)=>v&&setGroupBy(v)}>
            {GROUP_BY_OPTIONS.map(g=>(
              <ToggleButton key={g.key} value={g.key} sx={{ fontSize:11, py:0.5, px:1.5 }}>{g.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box flex={1}/>
          <Tooltip title="Refresh data">
            <Button size="small" variant="outlined" onClick={fetchAnalytics} startIcon={loading ? <CircularProgress size={14}/> : <RefreshIcon/>}>
              Refresh
            </Button>
          </Tooltip>
        </Paper>

        {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

        {loading && !chartData.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress/>
          </Box>
        ) : (
          <>
            <KpiCards/>
            <MainChart title={`Printing Department ${isPerMeter ? perMeterType : 'Energy'} – ${groupLabel}`}/>
            <SectionBreakdown/>
            <DataTable/>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsDashboard;
